import { Component, forwardRef, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StatusLeadService } from '../../../services/status-lead-service.service';
import { ContatoLead, LeadContatoService } from '../../../services/lead-contato-service.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateTimePickerComponent } from '../../calendar-date-time/datetime-picker.component';
import { StatusLead } from '../../../interfaces/status-lead.interface';
import { LocalStorageUtil } from '../../../utils/local-storage.util';

@Component({
  selector: 'app-leads-update-status-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    MatIconModule,
    DateTimePickerComponent,
  ],
  templateUrl: './leads-update-status-modal.html',
  styleUrls: ['./leads-update-status-modal.scss'],
})
export class LeadsUpdateStatusModal implements OnInit {
  form!: FormGroup;
  statusLeadList: StatusLead[] = [];
  minDate = new Date();
  loading = false;
  action: 'atualizar' | 'enviar' | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private snack: MatSnackBar,
    private statusLeadService: StatusLeadService,
    private leadContatoService: LeadContatoService,
    @Inject(MAT_DIALOG_DATA) public data: { idLead: number; cdSiglaTipo: string },
    public dialogRef: MatDialogRef<LeadsUpdateStatusModal>,
  ) {
    this.form = this.fb.group({
      status: ['', Validators.required],
      retorno: [{ value: null, disabled: true }, Validators.required],
      observacoes: ['', [Validators.required, Validators.maxLength(240)]],
    });
  }

  ngOnInit() {
    this.carregarStatus();

    this.form.get('status')?.valueChanges.subscribe((status) => {
      const retorno = this.form.get('retorno');

      const isNegociacao = status === 3;

      if (isNegociacao) {
        retorno?.enable();
      } else {
        retorno?.disable();
      }
    });
  }

  carregarStatus(): void {
    this.statusLeadService.getAllList().subscribe({
      next: (data) => {
        let ordemDesejada = null;

        if (this.data.cdSiglaTipo === 'SS') {
          ordemDesejada = [
            'Em negociação',
            'Assinatura efetivada',
            'Assinatura perdida',
            'Não foi possível contato',
          ];
        } else {
          // Ordem desejada dos status
          ordemDesejada = [
            'Em negociação',
            'Venda Perdida - Não foi possivel contato',
            'Venda Perdida - Proposta Reprovada',
            'Venda Perdida - Venda Outro Banco',
            'Venda Efetivada',
          ];
        }

        // Ordenar a lista baseado na ordem desejada
        this.statusLeadList = data
          .filter((item) => item.id !== 1)
          .sort((a, b) => {
            const indexA = ordemDesejada.indexOf(a.nome);
            const indexB = ordemDesejada.indexOf(b.nome);
            return indexA - indexB;
          });
      },
      error: () => console.error('Erro ao carregar status'),
    });
  }

  atualizar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.action = 'atualizar';
    const payload = {
      idLead: this.data.idLead,
      idStatusTo: this.form.value.status,
      dataRetorno: this.form.value.retorno,
      observacoes: this.form.value.observacoes,
      vendedor: LocalStorageUtil.getVWUser().name,
    } as ContatoLead;

    this.leadContatoService.atualizarContato(payload).subscribe({
      next: (res: any) => {
        this.snack
          .open('Status atualizado com sucesso!', 'OK', { duration: 2000 })
          .afterDismissed()
          .subscribe(() => {
            this.loading = false;
            this.dialogRef.close();
            this.router.navigate(['/dashboard']);
          });
      },

      error: (err) => {
        this.loading = false;

        // 400 ou 422 — erros de validação
        if (err.status === 400 || err.status === 422) {
          if (err.error?.errors) {
            this.aplicarErrosInline(err.error.errors);
          }
          return;
        }

        // 404 — lead não encontrado
        if (err.status === 404) {
          alert('Lead não encontrado.');
          return;
        }

        // 500 — erro genérico
        if (err.status === 500) {
          this.snack.open('Erro no servidor. Tente novamente.', 'OK', { duration: 3000 });
          return;
        }
      },
    });
  }

  enviarFicha() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.action = 'enviar';

    // simular chamada de API
    setTimeout(() => {
      this.loading = false;
      this.dialogRef.close(this.form.value);
    }, 2000);
  }

  aplicarErrosInline(errors: any) {
    Object.keys(errors).forEach((campo) => {
      const control = this.form.get(campo);
      if (control) {
        control.setErrors({ server: errors[campo] });
      }
    });
  }
}
